import React, { useState } from 'react';
import { Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

/**
 * Inline notice shown right above the Pro CTAs (Pricing page, UpgradeModal)
 * when the user hasn't confirmed their email yet. Mirrors backend behaviour:
 * the /api/stripe/checkout and /api/trial/start endpoints return 403 until
 * `email_verified === True`.
 *
 * Accepts a `theme` of 'dark' (Pricing page) or 'modal' (UpgradeModal).
 */
export default function EmailVerifyGate({ user, theme = 'dark' }) {
  const [busy, setBusy] = useState(false);
  if (!user || user.email_verified !== false) return null;

  const resend = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.resendVerification();
      toast.success(`Verification email sent to ${user.email}`);
    } catch (e) {
      toast.error(e?.message || 'Could not resend verification email');
    } finally {
      setBusy(false);
    }
  };

  const isModal = theme === 'modal';
  return (
    <div
      className={`mb-4 rounded-lg border p-3 flex items-start gap-2.5 ${
        isModal
          ? 'border-amber-400/30 bg-amber-400/[0.08] text-amber-100'
          : 'border-amber-400/30 bg-amber-400/[0.06] text-amber-100'
      }`}
      data-testid="email-verify-gate"
    >
      <Mail className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-300" />
      <div className="flex-1 min-w-0">
        <p className="text-[13px] leading-snug">
          Verify <strong className="font-semibold break-all">{user.email}</strong> to unlock Pro checkout.
        </p>
        <button
          type="button"
          onClick={resend}
          disabled={busy}
          className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] font-semibold text-amber-200 hover:text-white underline-offset-2 hover:underline disabled:opacity-50 transition-colors"
          data-testid="email-verify-gate-resend"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
          {busy ? 'Sending…' : 'Resend verification email'}
        </button>
      </div>
    </div>
  );
}
